import { IdSchema, SkillSchema } from "@pax/world";
import { z } from "zod";
import {
  AgentVersionId,
  CatalogSlug,
  GameId,
  ImageSet,
  PaginationQuery,
  PresetId,
  PresetVersionId,
  ProfileId,
  Timestamp,
  UserId,
} from "../../common";
import { ArtworkSet, LandingMedia } from "../../presets/v1alpha1/schemas";

const examples = {
  gameId: "9d2f6c1e-3b7a-4f0e-8c21-5a6d7e8f9a0b",
  userId: "0f3c1d7e-5a2b-4c8d-9e6f-1a2b3c4d5e6f",
} as const;

export const GameIdParams = z
  .strictObject({
    id: GameId,
  })
  .meta({
    id: "GameIdParams",
    description: "A route parameter naming one game.",
    example: { id: examples.gameId },
  });
export type GameIdParams = z.infer<typeof GameIdParams>;

export const GameMembership = z
  .strictObject({
    perspectiveHandle: z.string().nullable().meta({
      description: "The perspective the current user plays, or null while they spectate.",
    }),
    perspectiveName: z.string().nullable(),
    perspectiveImageUrl: z.url().nullable(),
    joinedAt: Timestamp,
    updatedAt: Timestamp,
  })
  .meta({
    id: "GameMembership",
    description:
      "The current user's access to one game, and the perspective the game server last reported them playing.",
  });
export type GameMembership = z.infer<typeof GameMembership>;

export const GamePreset = z
  .strictObject({
    id: PresetId,
    slug: CatalogSlug,
    title: z.string().min(1),
    cover: ArtworkSet.nullable(),
    landing: LandingMedia.nullable(),
    mapPreview: ArtworkSet.nullable().meta({
      description: "A still of the scenario's opening map, for a game that has none of its own.",
    }),
  })
  .meta({
    id: "GamePreset",
    description:
      "The scenario a game started from, as the version it was copied from published it. A game's own title drifts away from it the moment it is played, so both are carried.",
  });
export type GamePreset = z.infer<typeof GamePreset>;

/**
 * Everything a role can let its members do in a game. The owner can do all of it, and alone can
 * delete the game or hand it to someone else.
 */
export const GamePermission = z
  .enum([
    "renameGame",
    "manageRoles",
    "chat",
    "deleteMessages",
    "pinMessages",
    "changeGameMaster",
    "viewRules",
    "editRules",
    "forkGame",
    "voteToJump",
    "intervene",
    "useAdvisor",
    "manageJoining",
    "makeSpectators",
    "removePeople",
    "runJumps",
    "pauseGame",
    "changeWhoPays",
  ])
  .meta({ id: "GamePermission" });
export type GamePermission = z.infer<typeof GamePermission>;

/**
 * Who a game's link lets in. `link`: anyone with it. `friends`: the owner's friends, while anyone
 * else asks. `ask`: everyone asks, and a member whose roles allow `manageJoining` lets them in.
 */
export const WhoCanJoin = z.enum(["link", "friends", "ask"]).meta({ id: "WhoCanJoin" });
export type WhoCanJoin = z.infer<typeof WhoCanJoin>;

/**
 * Whether anyone else can be in a game. `singleplayer`: only its owner, as every game starts.
 * `reviewing`: being checked before it opens to others, and holding still meanwhile.
 * `multiplayer`: anyone its link lets in.
 */
export const GameMode = z
  .enum(["singleplayer", "reviewing", "multiplayer"])
  .meta({ id: "GameMode" });
export type GameMode = z.infer<typeof GameMode>;

export const GameSummary = z
  .strictObject({
    id: GameId,
    presetVersionId: PresetVersionId.meta({
      description: "The preset version whose world this game was copied from.",
    }),
    preset: GamePreset,
    title: z
      .string()
      .meta({ description: "The game's display title.", example: "Eve of Barbarossa" }),
    savedRevision: z.number().int().nonnegative().meta({
      description: "The latest revision the game server checkpointed to storage.",
      example: 42,
    }),
    savedAt: Timestamp.nullable().meta({
      description: "When that checkpoint was acknowledged, or null before the first one.",
    }),
    roundsCompleted: z.number().int().nonnegative().meta({
      description: "How many rounds the game has completed as of its latest checkpoint.",
      example: 3,
    }),
    isOwner: z.boolean().meta({
      description: "Whether the current member owns this game.",
    }),
    permissions: z.array(GamePermission).meta({
      description:
        "What the current member's roles let them do, from the API's copy of the game's lobby. The owner holds every permission.",
    }),
    memberCount: z.number().int().positive().meta({
      description: "How many people are in this game, the current user included.",
      example: 1,
    }),
    mode: GameMode,
    whoCanJoin: WhoCanJoin,
    joinRequestCount: z.number().int().nonnegative().meta({
      description:
        "How many people are asking to join, for a member whose roles allow `manageJoining`. 0 for anyone else.",
      example: 0,
    }),
    createdAt: Timestamp,
    updatedAt: Timestamp,
    membership: GameMembership,
  })
  .meta({
    id: "GameSummary",
    description: "One Postgres-backed game-directory entry with the current user's membership.",
  });
export type GameSummary = z.infer<typeof GameSummary>;

export const ListGamesQuery = PaginationQuery;
export type ListGamesQuery = z.infer<typeof ListGamesQuery>;

export const ListGamesResponse = z
  .object({
    items: z.array(GameSummary).meta({ description: "Newest first." }),
    nextCursor: z.string().nullable(),
  })
  .meta({
    id: "ListGamesResponse",
    description: "One page of games the current user is a member of.",
    example: { items: [], nextCursor: null },
  });
export type ListGamesResponse = z.infer<typeof ListGamesResponse>;

export const CreateGameRequest = z
  .strictObject({
    id: GameId.meta({
      description:
        "The new game's id, chosen by the caller so a retried create returns the same game.",
    }),
    presetVersionId: PresetVersionId.meta({
      description: "A published preset version, such as a listing's `liveVersionId`.",
    }),
    mode: z.enum(["singleplayer", "multiplayer"]).meta({
      description:
        "`singleplayer` for just the owner, or `multiplayer` for anyone its link lets in. A new game has nothing anyone wrote yet, so it opens to others without a review.",
    }),
  })
  .meta({
    id: "CreateGameRequest",
    description: "The new game's id, the preset version whose world it starts from, and its mode.",
    example: {
      id: examples.gameId,
      presetVersionId: "7e08d581-9de8-43ec-a294-f598cccbbaa7",
      mode: "singleplayer",
    },
  });
export type CreateGameRequest = z.infer<typeof CreateGameRequest>;

export const CreateGameResponse = z
  .object({
    game: GameSummary,
  })
  .meta({
    id: "CreateGameResponse",
    description: "The new game, with the owner as its only member.",
  });
export type CreateGameResponse = z.infer<typeof CreateGameResponse>;

export const GetGameResponse = z
  .object({
    game: GameSummary,
  })
  .meta({ id: "GetGameResponse", description: "One game available to the current user." });
export type GetGameResponse = z.infer<typeof GetGameResponse>;

/** A game's name, which members whose roles allow `renameGame` change. */
export const GameTitle = z.string().trim().min(1).max(200);

export const UpdateGameRequest = z
  .strictObject({
    title: GameTitle.optional().meta({ description: "Needs `renameGame`. Moderated." }),
    whoCanJoin: WhoCanJoin.optional().meta({ description: "Needs `manageJoining`." }),
  })
  .refine(
    request => request.title !== undefined || request.whoCanJoin !== undefined,
    "Name something to change.",
  )
  .meta({
    id: "UpdateGameRequest",
    description: "The game's settings to change. Anything left out stays as it is.",
    example: { title: "Balkan gambit" },
  });
export type UpdateGameRequest = z.infer<typeof UpdateGameRequest>;

export const UpdateGameResponse = z
  .object({ game: GameSummary })
  .meta({ id: "UpdateGameResponse", description: "The game, changed." });
export type UpdateGameResponse = z.infer<typeof UpdateGameResponse>;

export const SetGameModeRequest = z
  .strictObject({
    mode: z.enum(["singleplayer", "multiplayer"]).meta({
      description:
        "`multiplayer` opens the game to others once it passes review. `singleplayer` removes everyone but the owner.",
    }),
  })
  .meta({
    id: "SetGameModeRequest",
    description: "The mode to put the game in.",
    example: { mode: "multiplayer" },
  });
export type SetGameModeRequest = z.infer<typeof SetGameModeRequest>;

export const SetGameModeResponse = z
  .object({ game: GameSummary })
  .meta({ id: "SetGameModeResponse", description: "The game in its new mode." });
export type SetGameModeResponse = z.infer<typeof SetGameModeResponse>;

export const JoinGameResponse = z
  .discriminatedUnion("status", [
    z.object({
      status: z.literal("joined"),
      game: GameSummary,
    }),
    z.object({
      status: z.literal("asking"),
      title: z.string().meta({ description: "The game's title, to show while they wait." }),
    }),
    z.object({
      status: z.literal("singleplayer"),
      title: z.string().meta({ description: "The game's title, to say which game it is." }),
    }),
  ])
  .meta({
    id: "JoinGameResponse",
    description:
      "`joined` with the game when the link let them in. `asking` when someone in the game has to let them in first. `singleplayer` when the game isn't open to anyone else.",
  });
export type JoinGameResponse = z.infer<typeof JoinGameResponse>;

export const GameMemberParams = z.strictObject({ id: GameId, userId: UserId }).meta({
  id: "GameMemberParams",
  description: "Route parameters naming one game and one person.",
  example: { id: examples.gameId, userId: examples.userId },
});
export type GameMemberParams = z.infer<typeof GameMemberParams>;

/** The role every member holds. It can be changed but not deleted, and ranks below every other. */
export const EVERYONE_ROLE_ID = "player";

export const GameRole = z
  .strictObject({
    id: IdSchema,
    name: z.string().trim().min(1).max(60),
    rank: z.number().int().min(0).max(1_000).meta({
      description:
        "Members manage only people and roles ranked below their own highest. The everyone role is 0.",
    }),
    permissions: z
      .array(GamePermission)
      .refine(
        permissions => new Set(permissions).size === permissions.length,
        "A role lists each permission once.",
      ),
  })
  .meta({ id: "GameRole", description: "A named set of permissions, given to members." });
export type GameRole = z.infer<typeof GameRole>;

export const GameMember = z
  .strictObject({
    userId: UserId,
    profileId: ProfileId.nullable().meta({
      description: "The member's public profile, or null for a guest, who has none.",
    }),
    displayName: z.string().min(1).meta({
      description: "The profile's display name, or `Guest`.",
    }),
    avatar: ImageSet.nullable(),
    isOwner: z.boolean(),
    perspective: z
      .strictObject({
        handle: z.string().meta({
          description:
            "The perspective's handle. Each view shows it under its own alias for that handle.",
        }),
      })
      .nullable()
      .meta({ description: "The perspective the game server last reported them playing." }),
    joinedAt: Timestamp,
    lastOnlineAt: Timestamp.nullable().meta({
      description:
        "When they last left the game: the moment their last connection ended, once they stayed away 30 seconds. Null until they first leave.",
    }),
  })
  .meta({
    id: "GameMember",
    description:
      "Someone in a game and their profile. Who they play reaches the game's sockets as `members` frames.",
  });
export type GameMember = z.infer<typeof GameMember>;

export const ListMembersResponse = z
  .object({
    members: z
      .array(GameMember)
      .meta({ description: "The owner first, then by when they joined." }),
  })
  .meta({ id: "ListMembersResponse", description: "Everyone in the game." });
export type ListMembersResponse = z.infer<typeof ListMembersResponse>;

export const JoinRequest = z
  .strictObject({
    userId: UserId,
    profileId: ProfileId,
    displayName: z.string().min(1),
    avatar: ImageSet.nullable(),
    requestedAt: Timestamp,
  })
  .meta({ id: "JoinRequest", description: "Someone the game's link didn't let in, asking to." });
export type JoinRequest = z.infer<typeof JoinRequest>;

export const ListJoinRequestsResponse = z
  .object({
    requests: z.array(JoinRequest).meta({ description: "Oldest first." }),
  })
  .meta({ id: "ListJoinRequestsResponse", description: "Everyone asking to join the game." });
export type ListJoinRequestsResponse = z.infer<typeof ListJoinRequestsResponse>;

export const AnswerJoinRequestRequest = z
  .strictObject({
    answer: z.enum(["letIn", "turnAway"]).meta({
      description: "`letIn` makes them a member. `turnAway` ends the ask; they can ask again.",
    }),
  })
  .meta({ id: "AnswerJoinRequestRequest", example: { answer: "letIn" } });
export type AnswerJoinRequestRequest = z.infer<typeof AnswerJoinRequestRequest>;

export const SkillParams = z.strictObject({ id: GameId, skillId: IdSchema }).meta({
  id: "SkillParams",
  description: "Route parameters naming one game and one of its skills.",
  example: { id: examples.gameId, skillId: "rules" },
});
export type SkillParams = z.infer<typeof SkillParams>;

export const SkillChanges = SkillSchema.omit({ id: true })
  .partial()
  .meta({
    id: "SkillChanges",
    description: "The skill fields to change. Fields left out keep their value.",
    example: { body: "Turns are one month long." },
  });
export type SkillChanges = z.infer<typeof SkillChanges>;

export const UpdateSkillRequest = z
  .strictObject({
    changes: SkillChanges,
    basedOnRevision: z.number().int().positive().meta({
      description:
        "The revision the skill last changed at when the member began editing it, as the game socket's `skills` frame gives it. If it has changed since, the update is refused.",
      example: 41,
    }),
  })
  .meta({
    id: "UpdateSkillRequest",
    description: "The skill fields to change, and the version of the skill the change was made to.",
    example: { changes: { body: "Turns are one month long." }, basedOnRevision: 41 },
  });
export type UpdateSkillRequest = z.infer<typeof UpdateSkillRequest>;

/** One change to a game's skills, as the game server applies it. Rules are skills too. */
export const SkillEdit = z.discriminatedUnion("action", [
  z.strictObject({ action: z.literal("create"), skill: SkillSchema }),
  z.strictObject({
    action: z.literal("update"),
    skillId: IdSchema,
    changes: SkillChanges,
    basedOnRevision: UpdateSkillRequest.shape.basedOnRevision,
  }),
  z.strictObject({ action: z.literal("delete"), skillId: IdSchema }),
]);
export type SkillEdit = z.infer<typeof SkillEdit>;

export const SelectGameMasterRequest = z
  .strictObject({
    agentVersionId: AgentVersionId.meta({
      description: "A `liveVersionId` returned by `GET /api/agents/v1alpha1`.",
    }),
  })
  .meta({
    id: "SelectGameMasterRequest",
    description: "The agent version the game's new game master is created from.",
  });
export type SelectGameMasterRequest = z.infer<typeof SelectGameMasterRequest>;

export const ForkGameRequest = z
  .strictObject({
    gameId: GameId.meta({
      description: "The new game's id, chosen by the caller so a retried fork opens the same game.",
    }),
    throughRevision: z.number().int().positive().meta({
      description:
        "The last revision the new game keeps. It may not fall inside a jump; the revision just before a jump began keeps that round's actions queued.",
      example: 41,
    }),
  })
  .meta({
    id: "ForkGameRequest",
    description: "Where in this game's record the new game starts.",
    example: { gameId: examples.gameId, throughRevision: 41 },
  });
export type ForkGameRequest = z.infer<typeof ForkGameRequest>;

export const ForkGameResponse = z
  .object({
    game: GameSummary,
  })
  .meta({
    id: "ForkGameResponse",
    description: "The new game, owned by the caller and playing the same perspective.",
  });
export type ForkGameResponse = z.infer<typeof ForkGameResponse>;

export const LobbyCommandResponse = z
  .strictObject({
    revision: z.number().int().positive().meta({
      description: "The world revision the command committed through.",
      example: 43,
    }),
  })
  .meta({
    id: "LobbyCommandResponse",
    description: "Where the command landed in the world. The change also arrives on the socket.",
  });
export type LobbyCommandResponse = z.infer<typeof LobbyCommandResponse>;

export const UserGameMembership = z
  .object({
    gameId: GameId,
    title: z.string(),
    isOwner: z.boolean().meta({ description: "True when the user owns the game." }),
    lastActivityAt: Timestamp.meta({ description: "When the game last saw activity." }),
  })
  .meta({ id: "UserGameMembership", description: "One game a user owns or has joined." });
export type UserGameMembership = z.infer<typeof UserGameMembership>;

export const ListMembershipsQuery = z
  .strictObject({
    userId: UserId.meta({ description: "The user whose games to list." }),
    limit: z.coerce.number().int().min(1).max(100).default(25).meta({
      description: "Maximum games on the page, 1 to 100. Defaults to 25.",
      example: 25,
    }),
    cursor: GameId.optional().meta({
      description: "Read games older than this one: the `nextCursor` of the previous page.",
    }),
  })
  .meta({
    id: "ListMembershipsQuery",
    description: "Whose games to list, and where the page starts.",
  });
export type ListMembershipsQuery = z.infer<typeof ListMembershipsQuery>;

export const ListMembershipsResponse = z
  .object({
    memberships: z.array(UserGameMembership).meta({ description: "Most recently active first." }),
    nextCursor: GameId.nullable().meta({
      description: "The cursor for the next older page, or null on the last page.",
    }),
  })
  .meta({ id: "ListMembershipsResponse", description: "The games a user owns or has joined." });
export type ListMembershipsResponse = z.infer<typeof ListMembershipsResponse>;

export const GameInfo = z
  .object({
    id: GameId,
    title: z.string(),
    preset: z.object({ id: PresetId, title: z.string() }).meta({
      description: "The preset the game plays, with its title.",
    }),
    owner: z
      .object({
        userId: UserId,
        username: z.string().nullable().meta({
          description: "Their handle, or null while they have not claimed one.",
        }),
        displayName: z.string(),
      })
      .meta({ description: "Who owns the game." }),
    createdAt: Timestamp,
  })
  .meta({
    id: "GameInfo",
    description: "A game's title, preset, and owner, for staff who are not members.",
    example: {
      id: examples.gameId,
      title: "The Long Winter",
      preset: { id: "3b58d323-cbb8-57dd-ae77-1a9974b5afa5", title: "Europe 1914" },
      owner: { userId: examples.userId, username: "ada", displayName: "Ada" },
      createdAt: "2026-09-16T14:02:11.402Z",
    },
  });
export type GameInfo = z.infer<typeof GameInfo>;

export const GetGameInfoResponse = z
  .object({ game: GameInfo })
  .meta({ id: "GetGameInfoResponse", description: "One game's title, preset, and owner." });
export type GetGameInfoResponse = z.infer<typeof GetGameInfoResponse>;
